import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONSTANTS } from '../constants/Constants';

const StudentJitsiInterface = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classDetails, setClassDetails] = useState(null);
  const [joining, setJoining] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  
  useEffect(() => {
    // Fetch class details
    const fetchClassDetails = async () => {
      try {
        console.log('Fetching class details...', id);
        setLoading(true);
        const response = await axios.get(`${CONSTANTS.APIURL}/api/scheduled-classes/${id}`);
        setClassDetails(response.data);
        
        // Check if class is live
        if (response.data.status !== 'live') {
          setError('This class is not live yet. Please wait for the instructor to start the class.');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching class details:', err);
        setError(err.response?.data?.message || 'Error fetching class details');
        setLoading(false);
      }
    };
    
    fetchClassDetails();
  }, [id]);
  
  const handleJoinClass = async () => {
    try {
      setJoining(true);
      
      // Get join credentials
      const response = await axios.get(`${CONSTANTS.APIURL}/api/scheduled-classes/${id}/join`);
      
      const { meetingId, attendeePassword } = response.data;
      
      // Create direct join URL with password
      const displayName = encodeURIComponent('Student'); // You could use actual student name if available
      const directJoinUrl = `https://meet.jit.si/${meetingId}#config.prejoinPageEnabled=false&config.startWithVideoMuted=true&config.startWithAudioMuted=true&userInfo.displayName=${displayName}&password=${attendeePassword}`;
      
      setIframeSrc(directJoinUrl);
      setJoining(false);
    } catch (err) {
      console.error('Error joining class:', err);
      setError(err.response?.data?.message || 'Failed to join class');
      setJoining(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error && !iframeSrc) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-red-600 mb-2">Cannot Join Class</h1>
            <p className="text-gray-700">{error}</p>
          </div>
          
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/live-classes')}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
            >
              Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (classDetails && classDetails.status !== 'live' && !iframeSrc) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{classDetails.title}</h1>
            <p className="text-lg text-blue-600 mb-4">
              This class is not live yet
            </p>
            <p className="text-gray-700 mb-4">
              The instructor hasn't started the class yet. Please wait for the instructor to start the class or check back later.
            </p>
            
            <div className="bg-blue-50 p-4 rounded mb-6">
              <h2 className="font-semibold text-blue-800 mb-2">Class Details</h2>
              <p className="text-gray-700 mb-2">
                <span className="font-medium">Instructor:</span> {classDetails.instructor?.name}
              </p>
              <p className="text-gray-700 mb-2">
                <span className="font-medium">Scheduled Start:</span>{' '}
                {new Date(classDetails.scheduledStartTime).toLocaleString()}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Scheduled End:</span>{' '}
                {new Date(classDetails.scheduledEndTime).toLocaleString()}
              </p>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/live-classes')}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
            >
              Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-screen">
      <div className="bg-gray-800 text-white p-4">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">{classDetails?.title || 'Live Class'}</h1>
            <p className="text-sm text-gray-300">
              Instructor: {classDetails?.instructor?.name || 'Instructor'}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {!iframeSrc ? (
              <button
                onClick={handleJoinClass}
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
              >
                {joining ? 'Joining...' : 'Join Class Now'}
              </button>
            ) : (
              <button
                onClick={() => navigate('/live-classes')}
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded"
              >
                Leave Class
              </button>
            )}
            <button
              onClick={() => navigate('/live-classes')}
              className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded"
            >
              Back to Classes
            </button>
          </div>
        </div>
      </div>
      
      {!iframeSrc ? (
        <div className="flex-grow p-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Class Information</h2>
              <p className="text-gray-700 mb-6">{classDetails?.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded">
                  <h3 className="font-medium text-gray-800 mb-2">Schedule</h3>
                  <p className="text-gray-700">
                    <span className="font-medium">Starts:</span>{' '}
                    {classDetails ? new Date(classDetails.scheduledStartTime).toLocaleString() : ''}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">Ends:</span>{' '}
                    {classDetails ? new Date(classDetails.scheduledEndTime).toLocaleString() : ''}
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded">
                  <h3 className="font-medium text-gray-800 mb-2">Category</h3>
                  <p className="text-gray-700">{classDetails?.category}</p>
                </div>
              </div>
              
              <div className="text-center">
                <button
                  onClick={handleJoinClass}
                  className="bg-green-600 hover:bg-green-700 text-white py-3 px-8 rounded-lg font-medium"
                  disabled={joining}
                >
                  {joining ? (
                    <>
                      <span className="inline-block animate-spin mr-2">⟳</span>
                      Joining...
                    </>
                  ) : (
                    'Join Class Now'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-grow">
          <iframe 
            src={iframeSrc}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full h-full border-none"
            allowFullScreen
            title="Jitsi Meeting"
          />
        </div>
      )}
    </div>
  );
};

export default StudentJitsiInterface;